import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core'
import { distinctUntilChanged, filter, map, mergeMap, pluck, take, tap } from 'rxjs/operators'
import { DealConfigurationQuery } from '../../../../../../generated/graphql'
import { BehaviorSubject, combineLatest, merge, Observable, of, ReplaySubject } from 'rxjs'
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms'
import { DealMutationService } from '../../../../services/dealMutation.service'
import { ConfigurationCategoryService, ConfigurationOptionCategory } from '../../services/configurationCategory.service'
import { DealQueryService } from 'modules/deals/services/dealQuery.service'
import { DealOption, SelectableDealOption } from 'modules/deals/utilities/deal-types'
import { getDealOptionByKey, getDealOptionKey } from 'modules/deals/utilities/deal-helpers'
import { LocaleService } from 'framework/locale/services/locale.service'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { isFsdGrowthModelValue } from 'modules/deals/utilities/fsd-helpers'

@UntilDestroy()
@Component({
    selector: 'mg-configuration-options-factory',
    templateUrl: './configuration-options-factory.component.html',
    styleUrls: ['./configuration-options-factory.component.scss'],
})
export class ConfigurationOptionsFactoryComponent implements OnInit {
    @Input()
    form: FormGroup = new FormGroup({})

    @Input()
    optionBlacklist: string[] = []

    carPreviewFormFields$: BehaviorSubject<
        {
            formControl: FormControl
            configurationOption: DealOption
            id: string
            optionKey: string
            useCard: boolean
        }[]
    > = new BehaviorSubject([])

    remainingFormFields$: BehaviorSubject<
        {
            formControl: FormControl
            configurationOption: DealOption
            id: string
            optionKey: string
            useCard: boolean
        }[]
    > = new BehaviorSubject([])

    configurationOptions$: Observable<DealOption[]>
    configuration$: Observable<DealConfigurationQuery['Deal']>
    modelName$: Observable<string>
    private configurationId$: ReplaySubject<string> = new ReplaySubject<string>()
    private categories$: ReplaySubject<ConfigurationOptionCategory[]> = new ReplaySubject<
        ConfigurationOptionCategory[]
    >()

    language: string
    layoutTrigger$ = new BehaviorSubject(false)
    hasFsdGrowthModel: boolean = false

    constructor(
        private dealMutationService: DealMutationService,
        private dealQueryService: DealQueryService,
        private configurationCategoryService: ConfigurationCategoryService,
        private changeDetection: ChangeDetectorRef,
        private localeService: LocaleService
    ) {}

    private _configurationId: string

    get configurationId() {
        return this._configurationId
    }

    @Input()
    set configurationId(value: string) {
        if (value) {
            this.configurationId$.next(value)
            this._configurationId = value
        }
    }

    @Input() routeIndex: number

    @Input()
    set category(value: ConfigurationOptionCategory | ConfigurationOptionCategory[]) {
        this.categories$.next(Array.isArray(value) ? value : [value])
    }

    ngOnInit() {
        // current configuration
        this.configuration$ = this.configurationId$.pipe(
            untilDestroyed(this),
            filter(value => !!value),
            mergeMap(configurationId => this.dealQueryService.watchDealConfiguration(configurationId))
        )

        this.modelName$ = this.configuration$.pipe(
            untilDestroyed(this),
            pluck<DealConfigurationQuery['Deal'], string>('modelName'),
            map(modelName => (modelName || '').trim())
        )

        this.configurationOptions$ = combineLatest([
            this.configurationId$.pipe(filter(value => !!value)),
            this.categories$.pipe(filter(value => !!value && value.length > 0)),
        ]).pipe(
            untilDestroyed(this),
            mergeMap(([configurationId, categories]) => {
                return !categories
                    ? of([])
                    : this.configurationCategoryService.getConfigurationCategoryOptions(configurationId, categories)
            }),
            map(configurationOptions =>
                configurationOptions.reduce((options, option) => {
                    const optionKey = option.__typename === 'BooleanDealOption' ? option.key : option.name

                    if (optionKey && this.optionBlacklist.indexOf(optionKey) === -1) {
                        const newOption = {
                            ...option,
                            name: optionKey,
                        }
                        return [...options, newOption]
                    } else {
                        return options
                    }
                }, [])
            ),
            tap(configurationOptions => {
                this.hasFsdGrowthModel = this.checkIfHasFsdGrowthModel(configurationOptions)
            })
        )

        this.configurationOptions$.pipe(untilDestroyed(this)).subscribe((configurationOptions: DealOption[]) => {
            // all possible configurationOptionKeys from configuration options
            const configurationOptionKeys = configurationOptions.map(opt => getDealOptionKey(opt))

            // currently known form fields
            let formFields = [...this.carPreviewFormFields$.getValue(), ...this.remainingFormFields$.getValue()]

            // check if there are formFields which should be deleted
            // filter out removed fields, remove from form group
            formFields = formFields.filter(field => {
                if (configurationOptionKeys.indexOf(field.optionKey) === -1) {
                    this.form.removeControl(field.optionKey)
                    return false
                }

                return true
            })

            // update existing field with new configurationOption
            formFields = formFields.map(formField => {
                const configurationOption = getDealOptionByKey(configurationOptions, formField.optionKey)

                // only change if needed, this prevents reflows
                if (
                    JSON.stringify(configurationOption) !== JSON.stringify(formField.configurationOption) ||
                    formField.optionKey === 'downpayment'
                ) {
                    formField.configurationOption = configurationOption
                    formField.id = formField.configurationOption.id
                }
                return formField
            })

            // id's of currently know form fields
            const formFieldKeys = formFields.map(({ optionKey }) => optionKey)

            // create form field entries for new fields
            configurationOptions
                // filter out options for which a field is already created
                .filter(configurationOption => formFieldKeys.indexOf(getDealOptionKey(configurationOption)) === -1)

                // create a new form control
                .forEach(configurationOption => {
                    // create new form control
                    const formControl = this.createFormControl(configurationOption)
                    const optionKey = getDealOptionKey(configurationOption)

                    // add new formField to the queue
                    formFields.push({
                        formControl,
                        configurationOption,
                        id: configurationOption.id,
                        optionKey,
                        useCard: !['rims', 'paint', 'edition', 'steeringWheel'].includes(optionKey),
                    })
                })

            const { carPreviewFormFields, remainingFormFields } = formFields.reduce(
                (formFieldsObj, formField) => {
                    if (['edition', 'paint', 'rims', 'steeringWheel'].includes(formField.optionKey)) {
                        formFieldsObj.carPreviewFormFields = [...formFieldsObj.carPreviewFormFields, formField]
                    } else {
                        formFieldsObj.remainingFormFields = [...formFieldsObj.remainingFormFields, formField]
                    }

                    return formFieldsObj
                },
                { carPreviewFormFields: [], remainingFormFields: [] }
            )

            // publish new fieldset
            this.carPreviewFormFields$.next(carPreviewFormFields)
            this.remainingFormFields$.next(remainingFormFields)
            this.changeDetection.detectChanges()
            this.layoutTrigger$.next(true)
        })

        this.language = this.localeService.language
    }

    async syncFormValue() {
        // get latest configurationOptions after update
        combineLatest([
            this.configurationOptions$,
            this.dealMutationService.isUpdating.pipe(filter(isUpdating => !isUpdating)),
        ])
            .pipe(take(1))
            .subscribe(([configurationOptions]: [DealOption[], boolean]) => {
                // currently known form fields
                const formFields = [...this.carPreviewFormFields$.getValue(), ...this.remainingFormFields$.getValue()]

                formFields.forEach(formField => {
                    const configurationOption = configurationOptions.find(({ id }) => id === formField.id)

                    if (configurationOption) {
                        const value =
                            configurationOption.__typename === 'BooleanDealOption'
                                ? configurationOption.value
                                : configurationOption.selectedValue
                        if (value !== formField.formControl.value) {
                            formField.formControl.setValue(value)
                        }
                    }
                })
            })
    }

    trackFormField(index, formField) {
        return (formField && formField.optionKey) || index
    }

    /**
     * produces a formcontrol for a form field
     * @param configurationOption
     */
    private createFormControl(configurationOption: DealOption) {
        const value =
            configurationOption.__typename === 'BooleanDealOption'
                ? configurationOption.value
                : configurationOption.selectedValue.id
        const optionKey = getDealOptionKey(configurationOption)

        const validators: ValidatorFn[] = []

        if (optionKey === 'contactEmail') {
            validators.push(Validators.email)
        }

        // create new form control
        const formControl = new FormControl(value, { validators })

        // respond to changes in component, update value in configuration
        merge(formControl.statusChanges, formControl.valueChanges)
            .pipe(
                untilDestroyed(this),
                // make sure control is valid
                filter(i => formControl.valid && !formControl.pending),
                map(() => formControl.value),
                distinctUntilChanged()
            )
            .subscribe(() => this.formControlValueChange(optionKey))

        // add for future reference
        this.form.addControl(optionKey, formControl)

        return formControl
    }

    private async formControlValueChange(optionKey) {
        try {
            const formField = [...this.carPreviewFormFields$.getValue(), ...this.remainingFormFields$.getValue()].find(
                ({ optionKey: formFieldOptionKey }) => formFieldOptionKey === optionKey
            )
            await this.dealMutationService
                .updateDealOption(this.configurationId, formField.configurationOption, formField.formControl.value)
                .toPromise()
        } catch (e) {
            console.log(e)
        }
    }

    checkIfHasFsdGrowthModel(configurationOptions: DealOption[]) {
        const autopilotOption = configurationOptions.find(
            option => option.__typename === 'SelectableDealOption' && option.name === 'autopilot'
        )

        if (autopilotOption) {
            return (autopilotOption as SelectableDealOption).selectableValues.some(isFsdGrowthModelValue)
        }
    }

    useFsdGrowthModelComponent() {
        return this.hasFsdGrowthModel && this.localeService.language === 'nl'
    }

    hasAccessories() {
        return !!this.remainingFormFields$.getValue().find(el => el.optionKey.includes('accessory'))
    }
}
